import { c as _c } from "react/compiler-runtime";
import { useAppSelector } from "@store/store-helper";
import { fetchingProjectsFlagsSelector, filteredProjectMembersSelector, projectArchivingStateSelector } from "@store/projects/projects-selector";
import { UtilsTypes } from "@stellar/utils";
import { MEMBERS_TABLE_COLUMNS } from "@components/table/members/members-table-constants";
import { MembersTable } from "@components/table/members/members-table";
import { MembersSort } from "@components/table/members/members-sort";
import { DEFAULT_MEMBERS_SORT_BY, MEMBERS_SORT_BY_MAP } from "@components/table/members/members-sort-constants";
import { TeamTabProjectDetails } from "@pages/project-details/project-team/team-tab-project-details";
import { isMembersSortBy } from "@custom-types/type-guards";
import { useProjectTeamBulkActions } from "@pages/project-details/project-team/use-project-team-bulk-actions";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useCanAssignProjectRoles } from "@hooks/feature-control/use-can-assign-project-roles";
import { useMemo } from "react";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { getExportComponent, sortMembers } from "@utils/member-utils";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ProjectTeamMobileView } from "@pages/project-details/project-team/project-team-mobile-view";
import { searchSelector } from "@store/ui/ui-selector";
import { useMembersFilters } from "@components/table/members/members-filters/use-members-filters";
import { useDeselectHiddenMembers } from "@pages/group-details/use-deselect-hidden-members";
import { InviteMemberToProject } from "@pages/project-details/project-team/invite-member-to-project";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
/** Members columns without the "user" column */
const projectTeamMembersColumns = [...MEMBERS_TABLE_COLUMNS].filter(col => col !== "user" && col !== "joinedDate");

/** Column config for plans that may assign project roles, and for those that may not */
const COLUMNS_WITH_ROLE = {
  columns: projectTeamMembersColumns,
  defaultVisibleColumns: ["name", "email", "status", "role"]
};
const COLUMNS_WITHOUT_ROLE = {
  columns: projectTeamMembersColumns.filter(col => col !== "role"),
  defaultVisibleColumns: ["name", "email", "status"]
};

/**
 * Contains the Team tab of the project details
 */
export function ProjectTeam(t0) {
  const $ = _c(64);
  const {
    project,
    companyId,
    isLoading
  } = t0;
  const filteredProjectMembers = useAppSelector(filteredProjectMembersSelector);
  const {
    isFetchingProjects
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const canAssignProjectRoles = useCanAssignProjectRoles();
  let t1;
  if ($[0] !== project) {
    t1 = {
      project
    };
    $[0] = project;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    canInviteUsersToPrivateProject
  } = useHasUserValidPermissionProjectLevel(t1);
  const t2 = project?.id ?? "";
  let t3;
  if ($[2] !== t2) {
    t3 = projectArchivingStateSelector(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const projectArchivingState = useAppSelector(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      defaultSort: DEFAULT_MEMBERS_SORT_BY,
      isValidSort: isMembersSortBy
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t4);
  const effectiveSortBy = canAssignProjectRoles || MEMBERS_SORT_BY_MAP[sortBy].field !== "role" ? sortBy : DEFAULT_MEMBERS_SORT_BY;
  let t5;
  if ($[5] !== effectiveSortBy || $[6] !== filteredProjectMembers) {
    t5 = sortMembers(filteredProjectMembers, effectiveSortBy);
    $[5] = effectiveSortBy;
    $[6] = filteredProjectMembers;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const sortedProjectMembers = t5;
  let t6;
  if ($[8] !== canAssignProjectRoles || $[9] !== sortedProjectMembers) {
    t6 = {
      members: sortedProjectMembers,
      tableSubject: "project",
      canFilterByRole: canAssignProjectRoles
    };
    $[8] = canAssignProjectRoles;
    $[9] = sortedProjectMembers;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    filteredMembers,
    filterChips
  } = useMembersFilters(t6);
  useDeselectHiddenMembers(filteredMembers);
  let t7;
  if ($[11] !== companyId || $[12] !== filteredMembers || $[13] !== project) {
    t7 = {
      companyId,
      project,
      members: filteredMembers
    };
    $[11] = companyId;
    $[12] = filteredMembers;
    $[13] = project;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const buttonComponents = useProjectTeamBulkActions(t7);
  project?.archivingState;
  const shouldHideCheckbox = project?.archivingState === "ARCHIVED";
  const {
    columns,
    defaultVisibleColumns
  } = canAssignProjectRoles ? COLUMNS_WITH_ROLE : COLUMNS_WITHOUT_ROLE;
  let t8;
  if ($[15] !== isLoading || $[16] !== project) {
    t8 = <TeamTabProjectDetails project={project} isLoading={isLoading} />;
    $[15] = isLoading;
    $[16] = project;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = filteredMembers.length;
  const t10 = isFetchingProjects || isLoading;
  let t11;
  if ($[18] !== canInviteUsersToPrivateProject || $[19] !== project || $[20] !== projectArchivingState) {
    t11 = canInviteUsersToPrivateProject && projectArchivingState === UtilsTypes.ProjectArchivingState.active && project && <InviteMemberToProject project={project} buttonVariant="filled" />;
    $[18] = canInviteUsersToPrivateProject;
    $[19] = project;
    $[20] = projectArchivingState;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== canAssignProjectRoles || $[23] !== effectiveSortBy || $[24] !== filteredMembers.length || $[25] !== setSortBy) {
    t12 = filteredMembers.length > 0 && <MembersSort sortBy={effectiveSortBy} setSortBy={setSortBy} tableSubject="project" hiddenFields={canAssignProjectRoles ? undefined : ["role"]} />;
    $[22] = canAssignProjectRoles;
    $[23] = effectiveSortBy;
    $[24] = filteredMembers.length;
    $[25] = setSortBy;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== buttonComponents.export || $[28] !== filteredMembers.length) {
    t13 = getExportComponent(filteredMembers.length, buttonComponents.export);
    $[27] = buttonComponents.export;
    $[28] = filteredMembers.length;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== filterChips || $[31] !== filteredMembers.length || $[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t13) {
    t14 = <EntitiesToolbar toolbarEntityName="Member" numberOfRows={t9} isLoading={t10} filterChips={filterChips} mobileAction={t11} sortActions={t12} exportComponent={t13} />;
    $[30] = filterChips;
    $[31] = filteredMembers.length;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== buttonComponents || $[38] !== effectiveSortBy || $[39] !== filteredMembers || $[40] !== isLoading || $[41] !== isScreenSmOrSmaller || $[42] !== project) {
    t15 = isScreenSmOrSmaller && <ProjectTeamMobileView members={filteredMembers} isLoading={isLoading} bulkActionButtons={buttonComponents} selectedProject={project} sortBy={effectiveSortBy} />;
    $[37] = buttonComponents;
    $[38] = effectiveSortBy;
    $[39] = filteredMembers;
    $[40] = isLoading;
    $[41] = isScreenSmOrSmaller;
    $[42] = project;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== buttonComponents || $[45] !== canAssignProjectRoles || $[46] !== columns || $[47] !== companyId || $[48] !== debouncedSearchText || $[49] !== defaultVisibleColumns || $[50] !== effectiveSortBy || $[51] !== filteredMembers || $[52] !== isFetchingProjects || $[53] !== isLoading || $[54] !== isScreenSmOrSmaller || $[55] !== project || $[56] !== setSortBy || $[57] !== shouldHideCheckbox) {
    t16 = !isScreenSmOrSmaller && <ColumnPreferencesProvider key={canAssignProjectRoles ? "with-role" : "without-role"} tableKey="project-team-members-table" columns={columns} options={{
      nonConfigurableColumns: ["options"],
      defaultVisibleColumns
    }}><MembersTable key={debouncedSearchText} companyId={companyId} members={filteredMembers} project={project} tableSubject="project" isLoading={isFetchingProjects || isLoading} bulkActionButtons={buttonComponents} shouldHideCheckbox={shouldHideCheckbox} sortBy={effectiveSortBy} setSortBy={setSortBy} /></ColumnPreferencesProvider>;
    $[44] = buttonComponents;
    $[45] = canAssignProjectRoles;
    $[46] = columns;
    $[47] = companyId;
    $[48] = debouncedSearchText;
    $[49] = defaultVisibleColumns;
    $[50] = effectiveSortBy;
    $[51] = filteredMembers;
    $[52] = isFetchingProjects;
    $[53] = isLoading;
    $[54] = isScreenSmOrSmaller;
    $[55] = project;
    $[56] = setSortBy;
    $[57] = shouldHideCheckbox;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t14 || $[60] !== t15 || $[61] !== t16 || $[62] !== t8) {
    t17 = <>{t8}{t14}{t15}{t16}</>;
    $[59] = t14;
    $[60] = t15;
    $[61] = t16;
    $[62] = t8;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  return t17;
}